import React, { useEffect, useState } from "react";
import { Table, Spinner, Alert, Input, Button, Form, FormGroup, Label } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";


const PennyReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  // Filter States
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [status, setStatus] = useState("");
  const [filterType, setFilterType] = useState("customerId");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 30;
  const [hasNextPage, setHasNextPage] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  useEffect(() => {
    // Initial load: Fetch with empty filters (backend defaults to 'today')
    fetchPennySummary(1, {});
  }, []);

  const fetchPennySummary = async (page = 1, filters = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = { ...filters };
      params.page = page - 1; // Backend is zero-based
      params.size = rowsPerPage;

      // ✅ Uses the single API endpoint: URL.PENNY_REPORT
      const res = await axiosInstance.get(URL.PENNY_REPORT, { params });
      
      const resultData = Array.isArray(res.data) ? res.data : [];

      setData(resultData);
      setCurrentPage(page);
      setHasNextPage(resultData.length === rowsPerPage);
    } catch (err) {
      setError("Failed to fetch Penny reporting data. Please check if the API path is correct.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const filters = {};
    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    if (status) filters.status = status;
    if (filterType === "customerId" && customerId) filters.customerId = customerId;

    setActiveFilters(filters);
    fetchPennySummary(1, filters);
  };

  const handleExcelExport = async () => {
    if (data.length === 0) return alert("No data available to export!");

    setExporting(true);
    try {
      const params = { ...activeFilters, export: true };
      const response = await axiosInstance.get(URL.PENNY_REPORT, {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Penny_Report_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed. Check backend connectivity.");
    } finally {
      setExporting(false);
    }
  };

  const font10 = { fontSize: "10px" };
  const inputSmStyle = { fontSize: "11px", height: "30px", padding: "2px 6px" };
  const buttonSmStyle = { fontSize: "11px", padding: "4px 10px", height: "30px", lineHeight: "1" };

  return (
    <div style={font10}>
      <Form onSubmit={handleSearch} className="mb-3">
        <div className="d-flex align-items-end flex-wrap" style={{ gap: "10px" }}>
          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Filter Type</Label>
            <Input type="select" bsSize="sm" value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setCustomerId(""); }}
              style={inputSmStyle}>
              <option value="customerId">Customer ID</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Customer ID</Label>
            <Input bsSize="sm" type="number" value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              style={inputSmStyle} placeholder="ID" />
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Status</Label>
            <Input type="select" bsSize="sm" value={status} onChange={(e) => setStatus(e.target.value)} style={inputSmStyle}>
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">From Date</Label>
            <Input type="date" bsSize="sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>

          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">To Date</Label>
            <Input type="date" bsSize="sm" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>

          <div className="d-flex" style={{ gap: "6px" }}>
            <Button color="primary" type="submit" style={buttonSmStyle}>Search</Button>
            <Button color="success" onClick={handleExcelExport} style={buttonSmStyle} disabled={exporting}>
              {exporting ? <Spinner size="sm" /> : "Excel"}
            </Button>
          </div>
        </div>
      </Form>

      {loading ? (
        <div className="text-center p-4"><Spinner size="sm" /> Loading...</div>
      ) : error ? (
        <Alert color="danger" style={font10}>{error}</Alert>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <Table bordered striped hover responsive size="sm" className="mt-2 mb-0 text-nowrap">
              <thead className="thead-light">
                <tr>
                  <th>#</th>
                  <th>ID</th>
                  <th>Cust ID</th>
                  <th>Fintech</th>
                  <th>Account Number</th>
                  <th>IFSC</th>
                  <th>Merchant Ref</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th>Name per Bank</th>
                  <th>Penny ID</th>
                  <th>Type</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                {data.length > 0 ? (
                  data.map((item, index) => (
                    <tr key={item.id || index}>
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td>{item.id}</td>
                      <td>{item.customerId}</td>
                      <td>{item.fintech_name}</td>
                      <td>{item.bankAccountNumber}</td>
                      <td>{item.bankIfscCode}</td>
                      <td>{item.merchantReferenceId}</td>
                      <td>{item.status}</td>
                      <td>{item.message}</td>
                      <td>{item.nameAsPerBank}</td>
                      <td>{item.pennyId}</td>
                      <td>{item.type}</td>
                      <td>{item.createDate}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="13" className="text-center">No data found</td></tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-center mt-3">
            <div className="d-flex align-items-center" style={{ gap: "5px" }}>
              <Button color="outline-primary" size="sm" style={buttonSmStyle} 
                onClick={() => fetchPennySummary(currentPage - 1, activeFilters)} 
                disabled={currentPage === 1}>Prev</Button>
              <span className="mx-2">Page {currentPage}</span>
              <Button color="outline-primary" size="sm" style={buttonSmStyle} 
                onClick={() => fetchPennySummary(currentPage + 1, activeFilters)} 
                disabled={!hasNextPage}>Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PennyReport;